/**
 * Übersicht — die erste Seite nach dem Anmelden.
 *
 * Sie beantwortet drei Fragen ohne Klick: Ist alles versandbereit? Was ist
 * gerade in Arbeit? Und vor allem: was ist der nächste sinnvolle Schritt?
 *
 * Der letzte Punkt ist der wichtigste. Eine Übersicht, die nur Zahlen zeigt,
 * lässt jemanden ohne Einarbeitung ratlos zurück - deshalb steht hier immer
 * genau eine empfohlene Handlung, abgeleitet aus dem tatsächlichen Zustand.
 */

import { Status, Kampagnen, Ruecklauf, meldungVerstaendlich } from '../api.js';
import { el, ersetzen, karte, knopf, kennzeichen, laedt, meldung, zeitpunkt } from '../ui.js';

/**
 * Leitet den nächsten sinnvollen Schritt aus dem Zustand ab.
 *
 * @param {any} status
 * @param {any[]} kampagnen
 * @returns {{ titel: string, text: string, knopfText: string, ziel: string }}
 */
function naechsterSchritt(status, kampagnen) {
  const laufend = kampagnen.find((k) => k.status === 'laeuft');
  if (laufend) {
    return {
      titel: 'Eine Aussendung läuft gerade',
      text: `„${laufend.name}" wird versendet: ${laufend.gesendet} von ${laufend.gesendet + laufend.offen} Empfängern sind erreicht.`,
      knopfText: 'Fortschritt ansehen',
      ziel: '#/kampagnen',
    };
  }
  if (!status.bestand.kontakte) {
    return {
      titel: 'Zuerst brauchen Sie Empfänger',
      text: 'Laden Sie eine Liste aus Excel hoch. Wichtig ist eine Spalte mit der E-Mail-Adresse; Anrede, Titel und Nachname sorgen für die persönliche Ansprache.',
      knopfText: 'Empfänger hinzufügen',
      ziel: '#/kontakte',
    };
  }
  if (!status.bestand.vorlagen) {
    return {
      titel: 'Jetzt fehlt noch eine Vorlage',
      text: 'Bauen Sie Ihren Newsletter im Editor zusammen. Die Bausteine bringen die Gestaltung der Kanzlei bereits mit.',
      knopfText: 'Vorlage erstellen',
      ziel: '#/editor',
    };
  }
  return {
    titel: 'Sie können eine Aussendung starten',
    text: 'In vier Schritten: Vorlage wählen, Empfänger festlegen, Testmail prüfen, senden.',
    knopfText: 'Aussendung vorbereiten',
    ziel: '#/kampagnen/neu',
  };
}

/**
 * Baut die Karte zur Absenderdomain.
 *
 * Sie steht auf der Übersicht, weil ihr Ergebnis nicht an einer einzelnen
 * Aussendung hängt, sondern an der Domain — und weil es der größte einzelne
 * Hebel auf die Zustellbarkeit ist. Eine fehlerfrei gebaute Aussendung landet
 * trotzdem im Spam, wenn SPF, DKIM und DMARC nicht stimmen.
 *
 * Der Inhalt wird nachgeladen: die DNS-Abfragen dauern ein bis zwei Sekunden,
 * und so lange soll die Übersicht nicht leer bleiben.
 *
 * @returns {HTMLElement}
 */
function absenderKarte() {
  const inhalt = el('div', [laedt('Absenderdomain wird geprüft …')]);

  const karteElement = karte({
    titel: 'Absenderdomain',
    inhalt: [inhalt],
  });

  Status.absender()
    .then((bericht) => ersetzen(inhalt, ...absenderInhalt(bericht)))
    .catch((fehler) =>
      ersetzen(
        inhalt,
        meldung(
          'warnung',
          `Die Absenderdomain konnte nicht geprüft werden: ${
            fehler instanceof Error ? fehler.message : 'unbekannter Fehler'
          }`,
        ),
      ),
    );

  return karteElement;
}

/**
 * @param {any} bericht
 * @returns {HTMLElement[]}
 */
function absenderInhalt(bericht) {
  if (!bericht.domain) {
    return [
      meldung(
        'warnung',
        'Es ist keine Absenderadresse eingerichtet. Ohne sie lässt sich die Domain nicht prüfen.',
      ),
    ];
  }

  const fehler = bericht.befunde.filter((/** @type {any} */ b) => b.schwere === 'fehler');
  const warnungen = bericht.befunde.filter((/** @type {any} */ b) => b.schwere === 'warnung');

  /**
   * Drei Zeilen, je eine Frage. Die Kennzeichnung sagt auf einen Blick, was
   * steht — der Text darunter, was das bedeutet.
   *
   * @param {string} name
   * @param {'gruen'|'gelb'|'rot'|'neutral'} farbe
   * @param {string} zustand
   * @param {string} erlaeuterung
   * @returns {HTMLElement}
   */
  const zeile = (name, farbe, zustand, erlaeuterung) =>
    el('div.auswahl-eintrag', [
      el('span', [
        el('span.haupttext', { text: name }),
        el('span.nebentext', { text: erlaeuterung }),
      ]),
      el('span.rechts', [kennzeichen(zustand, farbe)]),
    ]);

  /** @type {HTMLElement[]} */
  const zeilen = [];

  // SPF — wer darf in Ihrem Namen senden.
  if (!bericht.spf.vorhanden) {
    zeilen.push(zeile('SPF', 'rot', 'fehlt', 'Legt fest, welche Server in Ihrem Namen senden dürfen.'));
  } else if (bericht.spf.anzahl > 1) {
    zeilen.push(zeile('SPF', 'rot', `${bericht.spf.anzahl} Einträge`, 'Zulässig ist genau einer — sonst scheitert die Prüfung bei jedem Empfänger.'));
  } else {
    const streng = bericht.spf.alles === '-';
    zeilen.push(
      zeile(
        'SPF',
        bericht.spf.alles === '+' || bericht.spf.alles === '?' ? 'gelb' : 'gruen',
        streng ? 'streng' : `endet auf „${bericht.spf.alles ?? '?'}all"`,
        'Legt fest, welche Server in Ihrem Namen senden dürfen.',
      ),
    );
  }

  // DKIM — die Signatur.
  zeilen.push(
    bericht.dkim.bestaetigt
      ? zeile(
          'DKIM',
          'gruen',
          bericht.dkim.selektoren.join(', '),
          'Signiert Ihre Nachrichten, damit sie unterwegs nicht verfälscht werden.',
        )
      : zeile(
          'DKIM',
          'gelb',
          'nicht bestätigt',
          'Kein bekannter Selektor gefunden — das heißt nicht zwingend, dass DKIM fehlt.',
        ),
  );

  // DMARC — was mit Fälschungen geschieht.
  if (!bericht.dmarc.vorhanden) {
    zeilen.push(zeile('DMARC', 'rot', 'fehlt', 'Gmail, Yahoo und Outlook.com verlangen ihn von Massenversendern.'));
  } else {
    const richtlinie = bericht.dmarc.richtlinie ?? 'unklar';
    zeilen.push(
      zeile(
        'DMARC',
        richtlinie === 'none' ? 'gelb' : 'gruen',
        `p=${richtlinie}`,
        richtlinie === 'none'
          ? 'Erfüllt die Mindestanforderung, wehrt Fälschungen aber nicht ab.'
          : 'Bestimmt, was mit Nachrichten geschieht, die sich als Ihre ausgeben.',
      ),
    );
  }

  /** @type {HTMLElement[]} */
  const teile = [
    el('p.editor-hinweis', {
      text: `Geprüft wurde ${bericht.domain}. Gelesen wird das DNS — ob der Versanddienst tatsächlich signiert, zeigt erst ein Probeversand.`,
    }),
    el('div.auswahl', zeilen),
  ];

  if (fehler.length || warnungen.length) {
    teile.push(
      el(
        'ul.befund-liste',
        bericht.befunde.map((/** @type {any} */ befund) =>
          el(`li.${befund.schwere}`, [
            el('strong', { text: befund.nachricht }),
            befund.hinweis ? el('span', { text: befund.hinweis }) : null,
          ]),
        ),
      ),
    );
  }

  if (!bericht.vollstaendig) {
    teile.push(
      meldung(
        'warnung',
        'Mindestens eine Abfrage ist technisch gescheitert. Der Befund ist damit unvollständig.',
      ),
    );
  }

  return teile;
}

/**
 * Baut die Karte zu den Rückmeldungen (Unzustellbarkeit, Beschwerden).
 *
 * Unzustellbarkeitsnachrichten treffen oft erst Stunden nach dem Versand
 * ein. Ohne diesen Abruf bleiben zu viele Adressen fälschlich als
 * "in Ordnung" markiert — er ist deshalb auf der Übersicht, nicht versteckt
 * in den technischen Angaben.
 *
 * @returns {HTMLElement}
 */
function ruecklaufKarte() {
  const ergebnisBereich = el('div');

  const abrufen = async () => {
    ersetzen(ergebnisBereich, laedt('Rückmeldungen werden abgerufen …'));
    try {
      const bilanz = await Ruecklauf.abrufen();
      ersetzen(ergebnisBereich, ...ruecklaufErgebnis(bilanz));
    } catch (fehler) {
      ersetzen(
        ergebnisBereich,
        meldung(
          'fehler',
          fehler instanceof Error ? fehler.message : 'Der Abruf ist fehlgeschlagen.',
        ),
      );
    }
  };

  return karte({
    titel: 'Rückmeldungen',
    inhalt: [
      el('p', {
        text:
          'Unzustellbarkeitsnachrichten und Beschwerden treffen oft erst Stunden nach dem Versand ein. ' +
          'Erst dieser Abruf trägt sie in die Empfängerliste ein — sonst bleiben betroffene Adressen ' +
          'fälschlich als „in Ordnung" markiert.',
      }),
      el('div.knopf-reihe', [knopf('Jetzt abrufen', { art: 'haupt', beiKlick: abrufen })]),
      ergebnisBereich,
    ],
  });
}

/**
 * @param {any} bilanz
 * @returns {Node[]}
 */
function ruecklaufErgebnis(bilanz) {
  const zahlen = el('div.kacheln', [
    el('div.kachel', [
      el('div.zahl', { text: String(bilanz.geprueft) }),
      el('div.beschriftung', { text: 'Geprüft' }),
    ]),
    el('div.kachel', [
      el('div.zahl', { text: String(bilanz.gesperrt) }),
      el('div.beschriftung', { text: 'Gesperrt' }),
    ]),
    el('div.kachel', [
      el('div.zahl', { text: String(bilanz.beschwerden) }),
      el('div.beschriftung', { text: 'davon Beschwerden' }),
    ]),
    el('div.kachel', [
      el('div.zahl', { text: String(bilanz.voruebergehend) }),
      el('div.beschriftung', { text: 'Vorübergehend' }),
    ]),
    el('div.kachel', [
      el('div.zahl', { text: String(bilanz.unklar) }),
      el('div.beschriftung', { text: 'Nicht zuordenbar — bleibt ungelesen' }),
    ]),
  ]);

  /** @type {Node[]} */
  const teile = [
    bilanz.geprueft
      ? meldung('erfolg', `${bilanz.geprueft} Nachrichten geprüft, ${bilanz.gesperrt} Adressen gesperrt.`)
      : meldung('info', 'Keine neuen Rückmeldungen seit dem letzten Abruf.'),
    zahlen,
  ];

  if (bilanz.vorgaenge?.length) {
    const artText = /** @type {Record<string, string>} */ ({
      hardbounce: 'Unzustellbar',
      beschwerde: 'Beschwerde',
      softbounce: 'Vorübergehend',
    });
    const artFarbe = /** @type {Record<string, 'rot'|'gelb'|'neutral'>} */ ({
      hardbounce: 'gelb',
      beschwerde: 'rot',
      softbounce: 'neutral',
    });
    teile.push(
      el(
        'div.auswahl',
        bilanz.vorgaenge.map((/** @type {any} */ vorgang) =>
          el('div.auswahl-eintrag', [
            el('span', [
              el('span.haupttext', { text: vorgang.email }),
              el('span.nebentext', { text: vorgang.grund }),
            ]),
            el('span.rechts', [
              kennzeichen(artText[vorgang.art] ?? vorgang.art, artFarbe[vorgang.art] ?? 'neutral'),
            ]),
          ]),
        ),
      ),
    );
  }

  return teile;
}

/**
 * @param {any} kampagne
 * @returns {HTMLElement}
 */
function zustandKennzeichen(kampagne) {
  const zuordnung = {
    entwurf: /** @type {const} */ (['Entwurf', 'neutral']),
    laeuft: /** @type {const} */ (['Läuft', 'blau']),
    fertig: /** @type {const} */ (['Versendet', 'gruen']),
    angehalten: /** @type {const} */ (['Angehalten', 'gelb']),
    welle_beendet: /** @type {const} */ (['Welle beendet', 'gelb']),
    fehler: /** @type {const} */ (['Fehler', 'rot']),
  };
  const [text, farbe] = zuordnung[/** @type {keyof typeof zuordnung} */ (kampagne.status)] ?? [
    kampagne.status,
    'neutral',
  ];
  return kennzeichen(text, farbe);
}

/**
 * @returns {import('../app.js').Ansicht}
 */
export function startAnsicht() {
  return {
    titel: 'Übersicht',
    async zeichnen(behaelter) {
      const [status, kampagnen] = await Promise.all([Status.laden(), Kampagnen.liste()]);
      const schritt = naechsterSchritt(status, kampagnen);
      const letzte = kampagnen.slice(0, 5);

      const kacheln = el('div.kacheln', [
        el('div.kachel', [
          el('div.zahl', { text: String(status.bestand.kontakte) }),
          el('div.beschriftung', { text: 'Empfänger' }),
        ]),
        el('div.kachel', [
          el('div.zahl', { text: String(status.bestand.vorlagen) }),
          el('div.beschriftung', { text: 'Vorlagen' }),
        ]),
        el('div.kachel', [
          el('div.zahl', { text: String(status.bestand.kampagnen) }),
          el('div.beschriftung', { text: 'Aussendungen' }),
        ]),
        el('div.kachel', [
          el('div.zahl', { text: String(status.bestand.gesperrt) }),
          el('div.beschriftung', { text: 'Abgemeldet / gesperrt' }),
        ]),
      ]);

      // Der Hinweiskasten ganz oben: eine Handlung, klar benannt.
      const empfehlung = karte({
        betont: true,
        titel: schritt.titel,
        inhalt: [
          el('p', { text: schritt.text }),
          el('div.knopf-reihe', [
            knopf(schritt.knopfText, {
              art: 'haupt',
              beiKlick: () => {
                location.hash = schritt.ziel;
              },
            }),
          ]),
        ],
      });

      const warnungen = [];
      if (!status.bereit) {
        warnungen.push(
          meldung(
            'warnung',
            `${meldungVerstaendlich(status.meldung)} Vorlagen lassen sich weiterhin bearbeiten, ein Versand ist aber noch nicht möglich.`,
          ),
        );
      }
      if (!status.massentauglich) {
        warnungen.push(
          meldung(
            'warnung',
            `Der eingestellte Versandweg „${status.provider}" ist nur für Testmails freigegeben. Microsoft schließt Newsletter über Exchange Online ausdrücklich aus — für Aussendungen ist Azure Communication Services oder ein SMTP-Versanddienst einzurichten.`,
          ),
        );
      }

      const verlauf = karte({
        titel: 'Letzte Aussendungen',
        aktion: knopf('Alle ansehen', {
          art: 'still',
          beiKlick: () => {
            location.hash = '#/kampagnen';
          },
        }),
        inhalt: letzte.length
          ? el(
              'div.auswahl',
              letzte.map((kampagne) =>
                el('div.auswahl-eintrag', [
                  el('span', [
                    el('span.haupttext', { text: kampagne.name }),
                    el('span.nebentext', {
                      text: `${kampagne.gesendet} versendet · ${kampagne.offen} offen${
                        kampagne.fehlgeschlagen ? ` · ${kampagne.fehlgeschlagen} Fehler` : ''
                      } · angelegt ${zeitpunkt(kampagne.erstellt_am)}`,
                    }),
                  ]),
                  el('span.rechts', [zustandKennzeichen(kampagne)]),
                ]),
              ),
            )
          : el('p.leer', { text: 'Noch keine Aussendung angelegt.' }),
      });

      // Die Vertiefung: alles Technische bleibt eingeklappt, ist aber da.
      const details = el('details.vertiefung', [
        el('summary', { text: 'Technische Angaben' }),
        el('div', [
          el('dl.kennwerte', [
            el('div', [
              el('dt', { text: 'Versandweg' }),
              el('dd', { text: status.provider }),
            ]),
            el('div', [
              el('dt', { text: 'Zustand (technisch)' }),
              el('dd', { text: status.meldung }),
            ]),
            el('div', [
              el('dt', { text: 'Sendekontingent' }),
              el('dd', {
                text: `${status.grenzen.proMinute}/Minute · ${status.grenzen.proStunde}/Stunde${
                  status.grenzen.proTag ? ` · ${status.grenzen.proTag}/Tag` : ''
                }`,
              }),
            ]),
            el('div', [
              el('dt', { text: 'KI-Assistent' }),
              el('dd', { text: status.kiAssistent ? 'eingerichtet' : 'nicht eingerichtet' }),
            ]),
          ]),
        ]),
      ]);

      ersetzen(
        behaelter,
        el('div.bahn', [
          el('div.seiten-kopf', [
            el('div', [
              el('h1', { text: 'Übersicht' }),
              el('p', {
                text: 'Newsletter für Mandanten — persönlich angesprochen, aus der eigenen Kanzlei-Domain versendet.',
              }),
            ]),
          ]),
          ...warnungen,
          kacheln,
          empfehlung,
          absenderKarte(),
          ruecklaufKarte(),
          verlauf,
          details,
        ]),
      );
    },
  };
}
